import { exitViewAs } from '@/lib/viewAs';
import { PRODUCT_NAME, PRODUCT_TAGLINE } from '@/lib/brand';
import { TeneanLockup } from '@/components/TeneanLockup';
import { parentOf, activeBarTo } from '@/lib/nav-parents';
import { NavLink, Link, Outlet, useLocation } from 'react-router-dom';
import { signOut, useViewAs } from '@/lib/auth';
import { useFeature } from '@/lib/useFeature';
import { LocationSelector } from '@/components/LocationSelector';
import { usePlatformFreeze } from '@/lib/platformFreeze';
import { FreezeBanner } from '@/components/FreezeBanner';

/**
 * TWO WINGS NAV (Peter, 2026-08-27: "A works, let's go with A then C" —
 * decision aa4fc89d, mockup dev.cadenceadvisers.com/design/pro-hub-ia.html).
 * Every pro question is one of three: my business, my studio, or me — so the
 * bar is four fixed doors: Home · Grow · Studio (renamed from My Studio on Peter's
 * 2026-08-28 call) ·
 * Me. Every future tool is a tile INSIDE the Grow wing, never a fifth door;
 * Maintenance and Documents live inside My Studio, one tap deeper. The More
 * sheet is gone — with four doors there is nothing left to overflow.
 * Mobile-optimized web; pros are WEB ONLY (no pro iOS app, ever).
 */
const NAV = [
  { to: '/',           label: 'Home',      icon: HomeIcon },
  // Grow stays gated by the 'grow' switchboard key — pilot one pro or one
  // studio with rows, not deploys (Peter, 2026-08-27). A studio without the
  // grant sees three doors; nothing else about the bar changes.
  { to: '/grow',       label: 'Grow',      icon: SproutIcon, feature: 'grow' },
  { to: '/my-studio',  label: 'Studio', icon: DoorIcon },
  { to: '/my-info',    label: 'Me',        icon: UserIcon },
];

function ViewAsBanner() {
  const { active, name, failed } = useViewAs();
  if (!active && !failed) return null;
  return (
    <div className="bg-amber-100 text-amber-900 text-xs font-bold px-4 py-2 flex items-center justify-between gap-3">
      <span>{failed ?? `\u{1F441} Viewing as ${name} \u2014 read only. Nothing you tap changes anything.`}</span>
      <button type="button" className="underline font-extrabold" onClick={exitViewAs}>Exit</button>
    </div>
  );
}

export default function Layout() {
  const location = useLocation();
  const freeze = usePlatformFreeze();
  // The bar and the desktop band render from this SAME filtered list, so the
  // two form factors cannot disagree about whether Grow exists.
  const growOn = useFeature('grow').enabled;
  const navItems = NAV.filter(n => !('feature' in n) || (n.feature === 'grow' && growOn));

  return (
    <div className="min-h-screen flex flex-col bg-image-fog">
      <ViewAsBanner />
      <FreezeBanner until={freeze ? freeze.until : undefined} />
      <header className="bg-image-black text-white">
        {/* THE HEADER SHARES THE PAGE'S COLUMN (Peter, 2026-08-29, circling the drift on a
            desktop screenshot: "IMAGE OS needs to move over to match the start of the page
            below; sign out also needs to come over and align with the right side"). The old
            lg:max-w-5xl widening existed for the 8-link nav that wrapped inside 3xl
            (2026-08-20); the Two Wings IA has four doors + Sign out, which fit the content
            column with room to spare — so the band and the page align edge to edge again. */}
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          {/* THE WAY BACK, SAID OUT LOUD (feedback d917f912 — Phil: "once you open any section,
              there is no button or option to navigate back"). The brand has always linked home,
              but an affordance you have to discover is not an affordance (the bulk-delete
              checkbox lesson). On every page below the hub itself, the header leads with an
              explicit door. */}
          {/* The brand lockup matches the Owner Hub's exactly (Peter, 2026-08-20:
              "Header should match this — IMAGE OS / Salon Operating System"):
              "IMAGE OS" with the space, over the platform line. The product name
              (Pro Hub) lives on the sign-in and the browser title, not here. */}
          {/* Type matches the Owner Hub's rail lockup EXACTLY (image-os-web
              src/shell.css:11-13): brand 24px/800/-.02em, platform line
              8.5px/700/.14em uppercase at white-45%. Read from the source,
              not eyeballed — Peter, 2026-08-20: "the font on the Salon
              Operating System must match what's on IMAGE OS." */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <div>
              <div className="whitespace-nowrap" aria-label={PRODUCT_NAME}>
                {/* The look paints this header paper-white (financy.css:126), so the lockup is the
                    on-LIGHT cut: on-dark here left every pro reading "Ten" with two coral cells
                    (the white cells and the white "ean" vanished into the white header, 2026-09-22). */}
                <TeneanLockup width={128} tone="light" />
              </div>
              <div className="text-[8.5px] font-bold tracking-[0.14em] uppercase text-white/45 whitespace-nowrap mt-1">
                {PRODUCT_TAGLINE}
              </div>
            </div>
          </Link>
          {/* Desktop top nav — the same four doors as the mobile bar.
              Capability-identical by construction: it renders from the SAME
              filtered navItems list. */}
          <div className="hidden lg:flex items-center gap-5 text-[13px] font-semibold">
            {navItems.map(n => (
              <NavLink key={n.to} to={n.to} end={n.to === '/'}
                className={({ isActive }) =>
                  `whitespace-nowrap ${isActive || n.to === activeBarTo(location.pathname)
                    ? 'text-image-pink border-b-2 border-image-pink pb-0.5'
                    : 'text-white/70 hover:text-image-pink transition-colors'}`}>
                {n.label}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {/* Multi-location selector. Self-hides for single-location Pros. */}
            <LocationSelector />

            <button
              onClick={() => signOut()}
              className="text-xs text-white/70 hover:text-image-pink transition-colors whitespace-nowrap"
              aria-label="Sign out"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      {location.pathname !== '/' && (
        <div className="lg:hidden bg-image-black/95 border-t border-white/10">
          <div className="max-w-3xl mx-auto px-5 py-1.5">
            <Link to={parentOf(location.pathname).to} aria-label={`Back to ${parentOf(location.pathname).label}`}
              className="text-xs font-semibold text-white/80 hover:text-image-pink transition-colors">
              Back to {parentOf(location.pathname).label}
            </Link>
          </div>
        </div>
      )}

      <main className="flex-1 max-w-3xl w-full mx-auto px-5 py-6 pb-28 md:pb-10">
        <Outlet />
      </main>

      <nav
        // z-30 is load-bearing: a fixed element with no stacking order can be
        // overlapped by in-flow content, and <main> (pb-28 reserves the space but
        // still spans the viewport) was intercepting taps on these links. Sits
        // ABOVE main, and deliberately BELOW the z-40/z-50 modal overlays so a
        // dialog still covers the nav.
        className="fixed bottom-0 inset-x-0 z-30 bg-image-black border-t border-image-pink/20 lg:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className={`max-w-3xl mx-auto grid ${navItems.length === 4 ? 'grid-cols-4' : 'grid-cols-3'}`}>
          {navItems.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                className={({ isActive }) =>
                  `flex flex-col items-center py-3 text-[11px] transition-colors ${
                    isActive || n.to === activeBarTo(location.pathname)
                      ? 'text-image-pink font-semibold'
                      : 'text-white/60 hover:text-image-pink'
                  }`
                }
              >
                <span className="mb-0.5"><Icon /></span>
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

// --- inline icons ---
const ip = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
function HomeIcon() { return (<svg {...ip}><path d="M3 11 12 3l9 8" /><path d="M5 10v10h14V10" /></svg>); }
function UserIcon() { return (<svg {...ip}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>); }
// A sprout — Grow's mark: something planted, coming up.
function SproutIcon() { return (<svg {...ip}><path d="M12 21v-8" /><path d="M12 13c0-4 3-7 8-7 0 4-3 7-8 7z" /><path d="M12 13c0-3-2.5-5.5-6.5-5.5 0 3.2 2.5 5.5 6.5 5.5z" /></svg>); }
// A storefront — My Studio's mark: the building, not the person.
function DoorIcon() { return (<svg {...ip}><path d="M3 9 5 4h14l2 5" /><path d="M4 9v11h16V9" /><path d="M9 20v-6h6v6" /></svg>); }
